import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  Phone,
  Settings,
  Shield,
  Users,
  TrendingUp,
  Home,
  Award,
  Clock,
  Quote,
  Star,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function HomePage() {
  return (
    <div className="font-['Plus_Jakarta_Sans']" data-testid="home-page">
      <SEO
        title={null}
        description="Momentum VA provides professional virtual assistant support for Australian real estate offices. Outsourced property management and sales admin — done right."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative bg-[#0B1C3E] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#2A9D8F] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5D6D35] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-[#2A9D8F] text-sm font-medium">
                <Award className="w-4 h-4 mr-2" />
                Trusted by Australian Real Estate Professionals
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] leading-tight">
                Outsourced Real Estate Support,{" "}
                <span className="text-[#2A9D8F]">Done Right</span>
              </h1>
              
              <p className="text-lg text-slate-300 leading-relaxed max-w-xl">
                Professional virtual assistants dedicated to Australian property management 
                and sales teams. We handle the admin, so you can focus on what matters.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    data-testid="hero-cta-book-call"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Book a Discovery Call
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                    data-testid="hero-cta-services"
                  >
                    View Services
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative hidden lg:block animate-fade-in animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_momentum-admin/artifacts/zj9v3atl_DSC05527-2.jpg"
                  alt="Professional virtual assistant team"
                  className="w-full h-[500px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1C3E]/60 to-transparent"></div>
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl max-w-[240px]">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#2A9D8F]" />
                  </div>
                  <span className="font-semibold text-[#0B1C3E]">Fully Managed</span>
                </div>
                <p className="text-slate-600 text-sm">
                  We handle hiring, training, and management—you get results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-slate-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">100+</div>
              <div className="text-slate-600 text-sm mt-1">Real Estate Offices Supported</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">500+</div>
              <div className="text-slate-600 text-sm mt-1">Hours Saved Monthly</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">99%</div>
              <div className="text-slate-600 text-sm mt-1">Client Retention Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">24/7</div>
              <div className="text-slate-600 text-sm mt-1">Support Availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Momentum VA */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              Real Estate Support You Can Trust
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              We're not a freelancer marketplace. We're your dedicated support partner 
              with processes built specifically for Australian real estate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group card-hover" data-testid="feature-card-0">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Settings className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Process-Driven</h3>
              <p className="text-slate-600">Systematic workflows tailored to Australian real estate operations.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group card-hover" data-testid="feature-card-1">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Quality Controlled</h3>
              <p className="text-slate-600">Dedicated oversight ensures consistent, high-quality output.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group card-hover" data-testid="feature-card-2">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Managed Teams</h3>
              <p className="text-slate-600">We manage your VA so you can focus on growing your business.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group card-hover" data-testid="feature-card-3">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Reliable Support</h3>
              <p className="text-slate-600">Consistent availability aligned with Australian business hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
                Our Services
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 mb-6 font-['Outfit']">
                Comprehensive Support for Your Office
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                From property management admin to sales coordination and graphic design to social media, we've got you covered 
                with trained professionals who understand Australian real estate & all the industry software.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4 p-6 bg-slate-50 rounded-xl border border-slate-100" data-testid="service-preview-0">
                  <div className="w-12 h-12 rounded-lg bg-[#5D6D35] flex items-center justify-center flex-shrink-0">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Property Management</h3>
                    <ul className="grid grid-cols-2 gap-2">
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Tenancy Administration
                      </li>
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Compliance Management
                      </li>
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Application Processing
                      </li>
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Lease Preparation
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-4 p-6 bg-slate-50 rounded-xl border border-slate-100" data-testid="service-preview-1">
                  <div className="w-12 h-12 rounded-lg bg-[#5D6D35] flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0B1C3E] mb-2 font-['Outfit']">Sales & Marketing</h3>
                    <ul className="grid grid-cols-2 gap-2">
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Appraisal Administration
                      </li>
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Pre List and List Admin
                      </li>
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        Marketing Co-ordination
                      </li>
                      <li className="flex items-center text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2A9D8F] mr-2 flex-shrink-0" />
                        CRM Management
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <Link to="/services" className="inline-block mt-8">
                <Button
                  className="bg-[#0B1C3E] hover:bg-[#0B1C3E]/90 text-white rounded-full px-6 font-semibold"
                  data-testid="view-all-services-btn"
                >
                  View All Services
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_momentum-admin/artifacts/8e0huw11_DSC05611-2.jpg"
                  alt="Professional team collaboration"
                  className="w-full h-[600px] object-cover object-[center_30%]"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#0B1C3E] text-white rounded-xl p-6 max-w-[280px] shadow-xl">
                <p className="font-medium mb-2 font-['Outfit']">
                  "Momentum VA transformed our office efficiency"
                </p>
                <p className="text-slate-300 text-sm">
                  — Real Estate Principal, Sydney
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Client Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              What Our Clients Say
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Real feedback from real estate professionals who trust Momentum VA
              to support their day-to-day operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Review 1 - Jessica */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="review-card-0">
              <div className="absolute top-6 right-6">
                <Quote className="w-8 h-8 text-[#2A9D8F]/15" />
              </div>
              <div className="flex gap-1 mb-4">
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "We genuinely value Momentum VA and our VA Archie and see them as an integral part of our team. We are very grateful for his ongoing support and are excited to continue growing with him as part of our business."
              </p>
              <div className="border-t border-slate-100 pt-4">
                <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Jessica Conte-Singh</p>
                <p className="text-sm text-slate-500">Stone Real Estate Whittlesea</p>
              </div>
            </div>

            {/* Review 2 - Paul */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="review-card-1">
              <div className="absolute top-6 right-6">
                <Quote className="w-8 h-8 text-[#2A9D8F]/15" />
              </div>
              <div className="flex gap-1 mb-4">
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "Momentum VA regularly keep in touch with me and supporting what we do. They constantly working on how they can take more work load from us."
              </p>
              <div className="border-t border-slate-100 pt-4">
                <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Paul Allam</p>
                <p className="text-sm text-slate-500">Stone Real Estate Newtown</p>
              </div>
            </div>

            {/* Review 3 - Ruby */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="review-card-2">
              <div className="absolute top-6 right-6">
                <Quote className="w-8 h-8 text-[#2A9D8F]/15" />
              </div>
              <div className="flex gap-1 mb-4">
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "Attentive and communicative from the beginning. I was pleasantly surprised by how well trained the team was prior to onboarding with our office."
              </p>
              <div className="border-t border-slate-100 pt-4">
                <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Ruby Johnson</p>
                <p className="text-sm text-slate-500">Stone Real Estate Crows Nest</p>
              </div>
            </div>

            {/* Review 4 - Rivka */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="review-card-3">
              <div className="absolute top-6 right-6">
                <Quote className="w-8 h-8 text-[#2A9D8F]/15" />
              </div>
              <div className="flex gap-1 mb-4">
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "We are incredibly happy with our move to Momentum VA and I have observed a considerate improvement to outsourcing our administration since changing suppliers."
              </p>
              <div className="border-t border-slate-100 pt-4">
                <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Rivka Goldberg</p>
                <p className="text-sm text-slate-500">Di Jones Real Estate</p>
              </div>
            </div>

            {/* Review 5 - Lisa */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="review-card-4">
              <div className="absolute top-6 right-6">
                <Quote className="w-8 h-8 text-[#2A9D8F]/15" />
              </div>
              <div className="flex gap-1 mb-4">
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
                <Star className="w-4 h-4 fill-[#E86C3F] text-[#E86C3F]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "The service is excellent."
              </p>
              <div className="border-t border-slate-100 pt-4">
                <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Lisa Scobie</p>
                <p className="text-sm text-slate-500">Forrest Beach Real Estate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-20 md:py-28 bg-white" data-testid="google-reviews-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <svg className="w-8 h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-[#0B1C3E] font-semibold text-sm uppercase tracking-wider font-['Outfit']">
                Google Reviews
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="text-2xl font-bold text-[#0B1C3E] font-['Outfit']">5.0</span>
              <div className="flex gap-0.5">
                <Star className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
              </div>
              <span className="text-slate-500 text-sm">Based on 2 reviews</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Google Review 1 - Meaghan */}
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="google-review-card-0">
              <div className="absolute top-6 right-6">
                <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div className="flex gap-0.5 mb-4">
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "Momentum are great, and Archie is our go-to person there. (We love you Archie!!) He's always a great help and a pleasure to deal with. Nothing is ever too hard for him, and he consistently goes out of his way to assist. He's kind, approachable, and reliable, and it really makes working with Momentum a smooth and positive experience."
              </p>
              <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Meaghan Butcher</p>
                  <p className="text-xs text-slate-400">May 2026</p>
                </div>
              </div>
            </div>

            {/* Google Review 2 - Chloe */}
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative card-hover" data-testid="google-review-card-1">
              <div className="absolute top-6 right-6">
                <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div className="flex gap-0.5 mb-4">
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "Momentum and especially Archie have been incredible to work with. The team is very knowledgeable and so helpful. We have absolutely loved working with them."
              </p>
              <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#0B1C3E] font-['Outfit']">Chloe</p>
                  <p className="text-xs text-slate-400">May 2026</p>
                </div>
              </div>
            </div>
          </div>

          {/* Review us on Google CTA */}
          <div className="text-center mt-10">
            <a
              href="https://www.google.com/maps/place/Momentum+VA/@15.1309367,120.5700383,17z/data=!3m1!4b1!4m6!3m5!1s0x3396f308270684f3:0xc9a952e251f40123!8m2!3d15.1309367!4d120.5726132!16s%2Fg%2F11n5421cht"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full text-[#0B1C3E] font-semibold text-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid="google-review-link"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Review us on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0B1C3E]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
            Ready to Streamline Your Operations?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Book a free discovery call to discuss how Momentum VA can support 
            your real estate office with dedicated virtual assistants.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg"
                data-testid="footer-cta-book-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                Book Your Free Call
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                data-testid="footer-cta-how-it-works"
              >
                See How It Works
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
